import React, { useEffect, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { BRAND_LUXE_MUTED, BRAND_SURFACE_CONTAINER_LOW } from '../brandDashboardTokens';
import { ManageGoldButton } from './manageShared';
import { listUserMediaAssets } from '../../../redux/userMedia/actions';
import { userMediaAssetsSelector, userMediaLoadingSelector } from '../../../redux/userMedia/selectors';
import { fetchBrandOpsUgcAssets } from '../../../redux/brandOps/actions';
import { brandOpsFetchingUgcAssetsSelector, brandOpsUgcAssetsSelector } from '../../../redux/brandOps/selectors';
import { getMyProjects } from '../../../redux/project/actions';
import { loadingProjectsSelector, userProjectsSelector } from '../../UserProjects/selector';
import type { IProject } from '../../../types/global/helper';

export type PickedAsset = { file_key?: string; url?: string };

type Tab = 'library' | 'ugc' | 'projects';

type Props = {
    open: boolean;
    onClose: () => void;
    brandId?: string;
    /** Video formats list video projects; image formats list picture projects */
    projectType: 'video' | 'picture';
    onPickAsset: (asset: PickedAsset) => void;
    onPickProject: (project: IProject) => void;
};

const thumbSx = { width: '100%', height: '100%', objectFit: 'cover' as const };
const rowSx = {
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 1.5,
    p: 1,
    display: 'flex',
    gap: 1,
    alignItems: 'center',
    bgcolor: 'transparent',
    cursor: 'pointer',
    textAlign: 'left' as const,
};

function Thumb({ src }: { src?: string | null }) {
    return (
        <Box sx={{ width: 56, height: 56, borderRadius: 1, overflow: 'hidden', bgcolor: '#222', flexShrink: 0 }}>
            {src ? <Box component="img" src={src} alt="" sx={thumbSx} /> : null}
        </Box>
    );
}

export default function ComposeMediaPicker({ open, onClose, brandId, projectType, onPickAsset, onPickProject }: Props) {
    const dispatch = useDispatch();
    const libraryAssets = useSelector(userMediaAssetsSelector);
    const libraryLoading = useSelector(userMediaLoadingSelector);
    const ugcAssets = useSelector(brandOpsUgcAssetsSelector);
    const ugcLoading = useSelector(brandOpsFetchingUgcAssetsSelector);
    const projects = useSelector(userProjectsSelector);
    const projectsLoading = useSelector(loadingProjectsSelector);
    const [tab, setTab] = useState<Tab>('library');

    useEffect(() => {
        if (!open) return;
        if (tab === 'library') {
            dispatch(listUserMediaAssets({ page: 1, limit: 48, append: false, force: true }));
        } else if (tab === 'ugc' && brandId) {
            dispatch(fetchBrandOpsUgcAssets({ brandId, force: true }));
        } else if (tab === 'projects') {
            dispatch(getMyProjects({ page: 1, limit: 40, type: projectType }));
        }
    }, [open, tab, dispatch, brandId, projectType]);

    const chooseProject = (p: IProject) => {
        const fileKey = (p as any).file_key as string | undefined;
        if (fileKey) onPickAsset({ file_key: fileKey, url: p.thumbnail || p.feature_image });
        else onPickProject(p);
    };

    const empty = (loading: boolean, count: number, text: string) =>
        !loading && !count ? (
            <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 13, mt: 1 }}>{text}</Typography>
        ) : null;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <Box sx={{ bgcolor: BRAND_SURFACE_CONTAINER_LOW, p: 2 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: '#fff', mb: 1.5 }}>Pick media</Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    {(
                        [
                            ['library', 'Library'],
                            ['ugc', 'UGC campaigns'],
                            ['projects', 'Projects'],
                        ] as const
                    ).map(([id, label]) => (
                        <ManageGoldButton
                            key={id}
                            onClick={() => setTab(id)}
                            sx={{
                                bgcolor: tab === id ? undefined : 'transparent',
                                color: tab === id ? undefined : BRAND_LUXE_MUTED,
                            }}>
                            {label}
                        </ManageGoldButton>
                    ))}
                </Box>

                {tab === 'library' ? (
                    <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                            {libraryAssets.map(asset => (
                                <Box
                                    key={asset.id || asset.file_key}
                                    component="button"
                                    onClick={() => onPickAsset(asset)}
                                    sx={{
                                        border: 0,
                                        p: 0,
                                        cursor: 'pointer',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        aspectRatio: '1',
                                        bgcolor: '#222',
                                    }}>
                                    {asset.url ? (
                                        <Box component="img" src={asset.url} alt={asset.name || ''} sx={thumbSx} />
                                    ) : null}
                                </Box>
                            ))}
                        </Box>
                        {libraryLoading && !libraryAssets.length ? (
                            <Typography sx={{ color: BRAND_LUXE_MUTED }}>Loading…</Typography>
                        ) : null}
                        {empty(libraryLoading, libraryAssets.length, 'No library images yet.')}
                    </>
                ) : null}

                {tab === 'ugc' ? (
                    <>
                        <Box
                            sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 360, overflow: 'auto' }}>
                            {ugcAssets.map(asset => (
                                <Box
                                    key={asset._id}
                                    component="button"
                                    onClick={() =>
                                        onPickAsset({
                                            file_key: asset.file_key || undefined,
                                            url: asset.preview_url || undefined,
                                        })
                                    }
                                    sx={rowSx}>
                                    <Thumb src={asset.preview_url} />
                                    <Box>
                                        <Typography sx={{ color: '#fff', fontSize: 13 }}>
                                            {asset.title || asset.file_key || 'Asset'}
                                        </Typography>
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                            {asset.campaign_title || 'Campaign'} · {asset.status || 'approved'}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        {ugcLoading && !ugcAssets.length ? (
                            <Typography sx={{ color: BRAND_LUXE_MUTED }}>Loading…</Typography>
                        ) : null}
                        {empty(ugcLoading, ugcAssets.length, 'No approved UGC assets for this brand yet.')}
                    </>
                ) : null}

                {tab === 'projects' ? (
                    <>
                        <Box
                            sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxHeight: 360, overflow: 'auto' }}>
                            {projects.map(p => (
                                <Box key={p._id} component="button" onClick={() => chooseProject(p)} sx={rowSx}>
                                    <Thumb src={p.thumbnail || p.feature_image} />
                                    <Box>
                                        <Typography sx={{ color: '#fff', fontSize: 13 }}>
                                            {p.title || 'Untitled'}
                                        </Typography>
                                        <Typography sx={{ color: BRAND_LUXE_MUTED, fontSize: 11 }}>
                                            {p.media_type || projectType} · {p.public_status || 'public'}
                                        </Typography>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                        {projectsLoading && !projects.length ? (
                            <Typography sx={{ color: BRAND_LUXE_MUTED }}>Loading…</Typography>
                        ) : null}
                        {empty(projectsLoading, projects.length, 'No projects found.')}
                    </>
                ) : null}
            </Box>
        </Dialog>
    );
}
